import React from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardBody, CardFooter, Button, Divider, addToast } from "@heroui/react";
import { Icon } from "@iconify/react";
import { PersonalInfoForm } from "./PersonalInfoForm";
import { MedicalQuestionsForm } from "./MedicalQuestionsForm";
import { DosageForm } from "./DosageForm";
import { DocumentsUploadForm } from "./DocumentsUploadForm";
import { TransportersForm } from "./TransportersForm";
import { RequestSchema, TRequest } from "@shared/entities";
import { useAppApi } from "src/appApi";
import { useStore } from "src/store/store";

type Step = {
	fields: Array<FieldPath<TRequest>>;
};
const steps: Array<Step> = [
	{
		fields: [
			"paymentDetails.cost",
			"serviceLevel",
			"clientDetails",
			"reasonType",
			"requestType",
			"isRecommendationOnly",
			"userComment",
		],
	},
	{
		fields: ["medicalQuestionnaire"],
	},
	{
		fields: ["dosages", "existsDosage"],
	},
	{
		fields: ["documents"],
	},
	{
		fields: ["transporters"],
	},
] as const;

export default function AdminCreateRequest() {
	const store = useStore();
	const appApi = useAppApi();
	const isBroker = store.user?.broker;

	const methods = useForm<TRequest>({
		resolver: zodResolver(RequestSchema),
		shouldUnregister: false,
		defaultValues: {
			id: crypto.randomUUID(),
			createdAt: Date.now(),
			status: "pending",
			userId: "", // Will be set by Cloud Function when user is created
			brokerId: isBroker ? store.user?.uid : "",
			createdBy: isBroker ? "broker" : "admin",
			isRecommendationOnly: false,
			// An admin filing for a client is full service by default.
			...(isBroker ? {} : { serviceLevel: "full" as const }),
			paymentDetails: {
				status: "pending",
			},
		},
	});

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = methods;

	const [currentStep, setCurrentStep] = React.useState(1);
	const totalSteps = steps.length;
	const isLastStep = currentStep >= totalSteps;

	const nextStep = async () => {
		if (isLastStep) {
			handleSubmit(onSubmit, (errors) => {
				appApi.api.system.uiLogs({
					message: "form error",
					severity: "ERROR",
					errors,
				});
			})();
			return;
		}
		const stepDetails = steps[currentStep - 1];

		if (!stepDetails) {
			return;
		}

		const isValid = await methods.trigger(stepDetails.fields);
		if (!isValid) {
			appApi.api.system.uiLogs({
				message: "step validation failed",
				severity: "ERROR",
				errors: methods.formState.errors,
			});
			addToast({
				title: "שגיאת אימות",
				description: "יש שדות שלא מולאו כראוי",
				color: "danger",
				variant: "solid",
			});
			return;
		}

		setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
	};

	const onSubmit = async (data: TRequest) => {
		const validation = RequestSchema.safeParse(data);
		if (!validation.success) {
			appApi.api.system.uiLogs({
				message: "form error",
				severity: "ERROR",
				errors: validation.error,
			});
			return;
		}

		// Here you would typically send the data to your API
		try {
			if (isBroker) {
				await appApi.api.broker.setRequest(data);
				appApi.navigate("/broker");
			} else {
				await appApi.api.admin.setRequest(data);
				appApi.navigate("/admin/requests");
			}
			// Reset form or redirect
		} catch (error) {
			console.error("Error submitting form:", error);
		}
	};

	const prevStep = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <PersonalInfoForm />;
			case 2:
				return <MedicalQuestionsForm />;
			case 3:
				return <DosageForm />;
			case 4:
				return <DocumentsUploadForm />;
			case 5:
				return <TransportersForm />;
			default:
				return <PersonalInfoForm />;
		}
	};

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return "פרטים אישיים";
			case 2:
				return "שאלון רפואי";
			case 3:
				return "מינון מבוקש";
			case 4:
				return "העלאת מסמכים";
			case 5:
				return "פרטי משנעים";
			default:
				return "";
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 p-4 md:p-6">
			<div className="max-w-4xl mx-auto">
				<Card className="mb-6">
					<CardHeader className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold">יצירת בקשה חדשה</h1>
						<p className="text-default-500">מילוי פרטי בקשה לרישיון קנאביס רפואי</p>
					</CardHeader>
				</Card>

				<div className="mb-6">
					<div className="flex justify-between items-center mb-2">
						<h2 className="text-lg font-semibold">{getStepTitle()}</h2>
						<div className="text-sm text-default-500">
							שלב {currentStep} מתוך {totalSteps}
						</div>
					</div>
					<div className="w-full bg-default-100 rounded-full h-2.5">
						<div
							className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${(currentStep / totalSteps) * 100}%` }}
						></div>
					</div>
				</div>

				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit, (errors) => {
							appApi.api.system.uiLogs({
								message: "form error",
								severity: "ERROR",
								errors,
							});
						})}
					>
						<Card className="mb-6">
							<CardBody>{renderStepContent()}</CardBody>
							<Divider />
							<CardFooter className="flex justify-between">
								<Button
									variant="flat"
									color="default"
									type="button"
									startContent={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
									onPress={prevStep}
									isDisabled={currentStep === 1}
								>
									הקודם
								</Button>

								<Button
									color={isLastStep ? "success" : "primary"}
									type={"button"}
									isLoading={isSubmitting}
									onPress={nextStep}
									endContent={
										isLastStep ? (
											<Icon icon="lucide:check" className="h-4 w-4" />
										) : (
											<Icon icon="lucide:arrow-left" className="h-4 w-4" />
										)
									}
									// isDisabled={!isDirty || !isValid}
								>
									{isLastStep ? "שלח בקשה" : "המשך"}
								</Button>
							</CardFooter>
						</Card>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}
